import React from "react";
import { Modal, Button, Progress, Typography } from "antd";
import { ClockCircleOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { THEME_COLORS, BORDER_RADIUS, SHADOWS } from "../utils/designSystem";

const { Title, Text, Paragraph } = Typography;

/**
 * SessionTimeoutModal
 * Displays an advance warning countdown when user inactivity is detected.
 * Complies with design system: borderRadius 16px, THEME_COLORS, mobile responsiveness.
 */
const SessionTimeoutModal = ({
  isOpen,
  countdown,
  totalWarningSeconds,
  isRefreshing,
  onStayLoggedIn,
  onLogoutNow,
}) => {
  if (!isOpen) return null;

  // Calculate percentage of remaining warning time (0% to 100%)
  const percent = totalWarningSeconds > 0
    ? Math.max(0, Math.min(100, Math.round((countdown / totalWarningSeconds) * 100)))
    : 0;

  const progressColor = countdown <= 10 ? THEME_COLORS.error : THEME_COLORS.warning;

  return (
    <Modal
      open={isOpen}
      closable={false}
      maskClosable={false}
      keyboard={false}
      centered
      footer={null}
      width={440}
      styles={{
        content: {
          borderRadius: BORDER_RADIUS.xl,
          boxShadow: SHADOWS.modal,
          padding: "28px 24px",
          textAlign: "center",
        },
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "16px" }}>
        {/* Warning Icon Badge */}
        <div
          style={{
            width: "60px",
            height: "60px",
            borderRadius: "50%",
            backgroundColor: countdown <= 10 ? THEME_COLORS.errorLight : THEME_COLORS.warningLight,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: progressColor,
            fontSize: "28px",
            transition: "all 0.3s ease",
          }}
        >
          {countdown <= 10 ? <ExclamationCircleOutlined /> : <ClockCircleOutlined />}
        </div>

        {/* Title */}
        <Title level={4} style={{ margin: 0, color: THEME_COLORS.textPrimary }}>
          Session Timeout Warning
        </Title>

        {/* Body Description */}
        <Paragraph style={{ color: THEME_COLORS.textSecondary, fontSize: "14px", margin: 0 }}>
          You have been inactive for a while. For your security, your session will automatically expire in:
        </Paragraph>

        {/* Countdown Progress Visualizer */}
        <div style={{ width: "100%", padding: "0 16px" }}>
          <div
            style={{
              fontSize: "36px",
              fontWeight: 700,
              color: progressColor,
              letterSpacing: "1px",
              fontVariantNumeric: "tabular-nums",
              marginBottom: "8px",
            }}
          >
            {countdown}s
          </div>
          <Progress
            percent={percent}
            showInfo={false}
            strokeColor={progressColor}
            trailColor={THEME_COLORS.border}
            size={["100%", 6]}
          />
        </div>

        <Text type="secondary" style={{ fontSize: "12px" }}>
          Click "Stay Logged In" to extend your active session.
        </Text>

        {/* Action Buttons */}
        <div
          style={{
            display: "flex",
            gap: "12px",
            width: "100%",
            marginTop: "8px",
            flexDirection: "row",
          }}
        >
          <Button
            block
            size="large"
            onClick={onLogoutNow}
            disabled={isRefreshing}
            style={{
              borderRadius: BORDER_RADIUS.base,
              borderColor: THEME_COLORS.border,
              color: THEME_COLORS.textSecondary,
              fontWeight: 500,
              height: "44px",
            }}
          >
            Log Out Now
          </Button>

          <Button
            type="primary"
            block
            size="large"
            loading={isRefreshing}
            onClick={onStayLoggedIn}
            style={{
              borderRadius: BORDER_RADIUS.base,
              backgroundColor: THEME_COLORS.primary,
              borderColor: THEME_COLORS.primary,
              fontWeight: 600,
              height: "44px",
            }}
          >
            Stay Logged In
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default React.memo(SessionTimeoutModal);
